import React from 'react';
import { Calendar, DollarSign, XCircle, CheckCircle, AlertCircle } from 'lucide-react';
import { RecurringService, forecastService } from '../../lib/forecastService';
import { useToast } from '../../contexts/ToastContext';

interface ServiceListProps {
  services: RecurringService[];
  onRefresh: () => void;
}

const ServiceList: React.FC<ServiceListProps> = ({ services, onRefresh }) => {
  const { showToast } = useToast();

  const handleCancelService = async (service: RecurringService) => {
    const endDate = prompt('Enter the end date (YYYY-MM-DD):');
    if (!endDate) return;

    const received = confirm('Was the final prorated payment received?');
    const notes = prompt('Add any notes about the cancellation (optional):');

    try {
      await forecastService.cancelService(service.id, endDate, received, notes || undefined);
      showToast('Service cancelled successfully', 'success');
      onRefresh();
    } catch (error) {
      showToast('Failed to cancel service', 'error');
      console.error('Error cancelling service:', error);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'completed':
        return <CheckCircle className="w-4 h-4 text-blue-600" />;
      case 'cancelled':
        return <XCircle className="w-4 h-4 text-red-600" />;
      default:
        return null;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'completed':
        return 'bg-blue-100 text-blue-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (services.length === 0) {
    return (
      <div className="text-center py-12 bg-gray-50 rounded-lg border border-gray-200">
        <DollarSign className="w-12 h-12 text-gray-400 mx-auto mb-3" />
        <p className="text-gray-600 font-medium">No recurring services</p>
        <p className="text-gray-500 text-sm mt-1">Add a recurring service to start tracking monthly revenue</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {services.map((service) => (
        <div
          key={service.id}
          className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-sm transition-shadow"
        >
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <h4 className="font-semibold text-gray-900">{service.service_name}</h4>
                <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(service.status)}`}>
                  {getStatusIcon(service.status)}
                  {service.status.charAt(0).toUpperCase() + service.status.slice(1)}
                </span>
              </div>
              <div className="mt-3 grid grid-cols-2 gap-4">
                <div className="flex items-center gap-2 text-sm">
                  <DollarSign className="w-4 h-4 text-gray-400" />
                  <span className="text-gray-600">
                    ${service.monthly_rate.toLocaleString('en-US', { minimumFractionDigits: 2 })}/month
                  </span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Calendar className="w-4 h-4 text-gray-400" />
                  <span className="text-gray-600">
                    Billing day: {service.billing_day}
                  </span>
                </div>
              </div>
              <div className="mt-2 text-sm text-gray-600">
                Started: {new Date(service.contract_start_date).toLocaleDateString()}
                {service.contract_end_date && ` • Ended: ${new Date(service.contract_end_date).toLocaleDateString()}`}
              </div>
              {service.prorated_amount && (
                <div className="mt-2 flex items-center gap-2 text-sm">
                  <AlertCircle className="w-4 h-4 text-orange-600" />
                  <span className="text-gray-600">
                    Final prorated payment: ${service.prorated_amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                    {service.final_payment_received ? ' (Received)' : ' (Not Received)'}
                  </span>
                </div>
              )}
            </div>
            {service.status === 'active' && (
              <button
                onClick={() => handleCancelService(service)}
                className="ml-4 px-3 py-1 text-sm text-red-600 bg-red-50 rounded-lg hover:bg-red-100 transition-colors"
              >
                Cancel
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

export default ServiceList;